/* app/bi-products.jsx - Stage 5: Products module (offerings + providers).
   Reuses BiModule shell via cfg overrides (CardComponent/RowComponent/DetailComponent).
   Plus the "Gaps and ideas" panel that sits under the register: what the
   providers you track prove you do not sell, and what a live search proposes
   next. The two are drawn apart on purpose, see GapsAndIdeas below. */
import { api } from '../lib/api.js';
import { newRunId, pollRun } from '../lib/run.js';
(function () {
  const { useState, useEffect, useCallback, useRef } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const H = () => window.MlBiHelpers;

  const PROV = { us: ["star", "us"], comp: ["flag", "comp"], partner: ["git-branch", "partner"], market: ["globe", "market"] };
  const OUR_PIPE = [["idea", "Idea"], ["planned", "Planned"], ["live", "Live"], ["retired", "Retired"]];
  const OTHER_PIPE = [["consideration", "Consideration"], ["offered", "Offered"], ["dropped", "Dropped"]];
  const PIPE_COLOR = { idea: "var(--gray-400)", planned: "var(--blue-500)", live: "var(--green-500)", retired: "var(--gray-400)", consideration: "var(--gray-400)", offered: "var(--blue-500)", dropped: "var(--red-500)" };

  function provCounts(providers) {
    const c = { us: 0, comp: 0, partner: 0, market: 0 };
    (providers || []).forEach(p => c[p.kind]++);
    return c;
  }
  function PipeSelect({ e, onFacet, down }) {
    const opts = e.ours ? OUR_PIPE : OTHER_PIPE;
    const FD = window.MlBiHelpers.FacetDropdown;
    return <FD label="Status" value={e.pipeline} opts={opts} down={down} colorOf={v => PIPE_COLOR[v] || "var(--text-secondary)"} onChange={onFacet ? (val) => onFacet(e.id, "pipeline", val) : undefined} />;
  }

  // ---- Product card (grid + row reuse) ----
  function ProductCard({ cfg, e, selectMode, selected, onToggle, onOurs, onOpen, onFacet }) {
    const { valBadge, srcBadge } = H();
    const [open, setOpen] = useState(false);
    const c = provCounts(e.providers);
    const cls = ["bi-card"];
    if (e.status === "pending") cls.push("bi-card--pending");
    if (e.status === "archived") cls.push("bi-card--archived");
    if (e.ours) cls.push("bi-card--ours");
    const card = (
      <div className={cls.join(" ")} tabIndex={0} onClick={() => onOpen && onOpen(e)}>
        <div className="bi-card-head">
          <div className="bi-card__name">{e.ours && <I name="star" size={15} className="bi-ours-star" style={{ fill: "currentColor" }} />}{e.name}</div>
          <div className="bi-card-badges">{valBadge(e.status)}{e.coverage === "gap" && <span className="bi-badge bi-badge--alt"><I name="alert-triangle" size={11} />Gap</span>}{srcBadge(e.source)}</div>
        </div>
        <div className="bi-card__meta"><div className="bi-meta-row"><span className="bi-meta-row__l">Service line</span><span className="bi-meta-row__v">{e.serviceLine}</span></div><div className="bi-meta-row"><span className="bi-meta-row__l">Market</span><span className="bi-meta-row__v">{e.market}</span></div></div>
        <div className="bi-prov-pills" onClick={ev => { ev.stopPropagation(); setOpen(o => !o); }}>
          <span className="bi-prov-pills__l">Offered by:</span>
          {c.us > 0 && <span className="bi-prov-pill bi-prov-pill--us"><I name="star" size={12} style={{ fill: "currentColor" }} />Us</span>}
          {c.comp > 0 && <span className="bi-prov-pill bi-prov-pill--comp"><I name="flag" size={12} />{c.comp} competitor{c.comp > 1 ? "s" : ""}</span>}
          {c.partner > 0 && <span className="bi-prov-pill bi-prov-pill--partner"><I name="git-branch" size={12} />{c.partner} partner{c.partner > 1 ? "s" : ""}</span>}
          {c.market > 0 && <span className="bi-prov-pill bi-prov-pill--market"><I name="globe" size={12} />{c.market} market</span>}
          <I name={open ? "chevron-up" : "chevron-down"} size={13} style={{ color: "var(--text-muted)" }} />
        </div>
        {open && <div className="bi-prov-list" onClick={ev => ev.stopPropagation()}>{e.providers.map((p, i) => (
          <div className={"bi-prov-row" + (p.kind === "us" ? " bi-prov-row--us" : "")} key={i}>
            <span className="bi-prov-row__icon"><I name={PROV[p.kind][0]} size={14} style={p.kind === "us" ? { fill: "currentColor", color: "var(--indigo-600)" } : null} /></span>
            <span className="bi-prov-row__name">{p.name}{p.tracked && <span className="bi-prov-row__tracked"><I name="arrow-up-right" size={11} />tracked</span>}</span>
            <span className="bi-prov-row__price">{p.price}</span>
            <span className="bi-prov-row__meta">{p.seen}</span>
          </div>
        ))}</div>}
        {e.variants && <div className="bi-variants-row"><I name="chevron-right" size={13} className="bi-chev" />{e.variants.length} sub-products · roll-up: <b>★us · {e.providers.filter(p => p.kind === "comp").length} comp</b></div>}
        <div className="bi-card-foot">
          <div className="bi-card-foot__left"><PipeSelect e={e} onFacet={onFacet} down /></div>
          <div className="bi-card-foot__right">
            <button className={"bi-iconbtn bi-ours-toggle" + (e.ours ? " is-ours" : "")} title={e.ours ? "Ours" : "Mark as ours"} onClick={ev => { ev.stopPropagation(); onOurs(e.id); }}><I name="star" size={16} /></button>
            <button className="bi-iconbtn" title="Move to…" onClick={ev => ev.stopPropagation()}><I name="shuffle" size={16} /></button>
            <span className="bi-viewdetails">View details<I name="arrow-right" size={14} /></span>
          </div>
        </div>
      </div>
    );
    if (!selectMode) return card;
    return <div className={"bi-select-wrap bi-select-wrap--on" + (selected ? " bi-select-wrap--selected" : "")}><label className="hds-check bi-select-wrap__check" onClick={ev => ev.stopPropagation()}><input type="checkbox" checked={selected} onChange={() => onToggle(e.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>{card}</div>;
  }

  // ---- Product detail ----
  function ProductDetail({ cfg, e, site, onSaved, onBack, onOurs, onStatus, onDelete, onEdit, onFacet }) {
    const { valBadge, srcBadge } = H();
    const [provModal, setProvModal] = useState(false);
    const archived = e.status === "archived";
    const st = (s) => onStatus && onStatus(e.id, s);
    return (
      <div className="bi-detail">
        <button className="bi-detail__back" onClick={onBack}><I name="chevron-left" size={16} />Back to Products</button>
        {e.coverage === "dup" && <div className="bi-dup-banner"><I name="alert-triangle" size={18} /><span className="bi-dup-banner__t">Possible duplicate of <b>VAT filing</b> - same service line.</span><button className="bi-btn bi-btn--sm">Merge into…</button><button className="bi-btn bi-btn--sm">Keep separate</button></div>}
        <div className="bi-detail-head">
          <div><div className="bi-detail__name">{e.ours && <I name="star" size={20} className="bi-ours-star" />}{e.name}</div><div className="bi-detail__badges">{valBadge(e.status) || <span className="bi-badge bi-badge--validated"><I name="check" size={11} />Validated</span>}{srcBadge(e.source)}</div></div>
          <div className="bi-detail-actions">
            {e.status === "validated" ? <button className="bi-btn" onClick={() => st("pending")}><I name="clock" size={16} />Move to pending</button> : <button className="bi-btn bi-btn--primary" onClick={() => st("validated")}><I name="check-circle" size={16} />Validate</button>}
            {archived ? <button className="bi-btn" onClick={() => st("pending")}><I name="rotate-ccw" size={16} />Restore</button> : <button className="bi-btn" onClick={() => st("rejected")}><I name="archive" size={16} />Archive</button>}
            <button className="bi-btn" onClick={() => onEdit && onEdit(e)}><I name="edit" size={16} />Edit</button>
            <button className="bi-btn bi-btn--danger" onClick={() => onDelete && onDelete(e.id)}><I name="trash" size={16} />Delete</button>
          </div>
        </div>

        <div className="bi-detail-section">
          <div className="bi-detail-section__h">Offering</div>
          <div className="bi-detail-section__body">
            <div className="bi-detail-row"><span className="bi-detail-row__l">Service line</span><span className="bi-detail-row__v">{e.serviceLine}</span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">Market</span><span className="bi-detail-row__v">{e.market}</span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">Pricing</span><span className="bi-detail-row__v">{e.providers.find(p => p.kind === "us") ? e.providers.find(p => p.kind === "us").price : "-"}</span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">Positioning</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{e.positioning}</span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">We offer</span><span className="bi-detail-row__v"><button className={"bi-iconbtn bi-ours-toggle" + (e.ours ? " is-ours" : "")} onClick={() => onOurs(e.id)} style={{ width: "auto", padding: "0 8px", gap: 6, fontSize: "var(--text-xs)", fontWeight: 600 }}><I name="star" size={14} />{e.ours ? "Yes - we offer this" : "No - opportunity"}</button></span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">Status</span><span className="bi-detail-row__v"><PipeSelect e={e} onFacet={onFacet} down /></span></div>
          </div>
        </div>

        <div className="bi-detail-section">
          <div className="bi-detail-section__h">Providers <span className="bi-conf">{e.providers.length}</span><button className="bi-btn bi-btn--sm" onClick={() => setProvModal(true)}><I name="plus" size={14} />Add provider</button></div>
          <div className="bi-detail-section__body"><div className="bi-prov-list">{e.providers.map((p, i) => (
            <div className={"bi-prov-row" + (p.kind === "us" ? " bi-prov-row--us" : "")} key={i}>
              <span className="bi-prov-row__icon"><I name={PROV[p.kind][0]} size={14} style={p.kind === "us" ? { fill: "currentColor", color: "var(--indigo-600)" } : null} /></span>
              <span className="bi-prov-row__name">{p.name}{p.tracked && <span className="bi-prov-row__tracked"><I name="arrow-up-right" size={11} />tracked</span>}</span>
              <span className="bi-prov-row__price">{p.price}</span>
              <span className="bi-prov-row__meta">{p.seen}</span>
              <div style={{ display: "flex", gap: 2 }}>{p.kind !== "us" && <button className="bi-iconbtn" title="Promote to us"><I name="star" size={15} /></button>}<button className="bi-iconbtn" title="Edit"><I name="edit" size={15} /></button><button className="bi-iconbtn" title="Remove"><I name="x" size={15} /></button></div>
            </div>
          ))}</div></div>
        </div>

        <div className="bi-detail-section">
          <div className="bi-detail-section__h">Placement</div>
          <div className="bi-detail-section__body"><div className="bi-detail-row" style={{ borderBottom: "none" }}><span className="bi-detail-row__l">Parent</span><span className="bi-detail-row__v" style={{ gap: 10 }}><select className="hds-select" style={{ maxWidth: 240, height: 34 }} defaultValue="none"><option value="none">Top level (no parent)</option><option>Tax &amp; VAT</option><option>Company formation</option></select><button className="bi-btn bi-btn--sm">Move</button></span></div></div>
        </div>

        {provModal && window.MlBiModals && <window.MlBiModals.ProviderModal entity={e} site={site} onSaved={onSaved} onClose={() => setProvModal(false)} />}
      </div>
    );
  }

  // ---- Gaps and ideas ----
  //
  // Two lists, never one. A GAP is arithmetic over the providers already in the
  // register, so it is stated as a fact and shows the named competitors it was
  // counted from. An IDEA is a proposal a model made from a web search, so it is
  // labelled as a proposal, carries the page it came from, and is never sorted
  // in among the gaps. Same rule the taxonomy audit keeps between a string match
  // and a model's opinion: presenting them with equal weight makes the weaker
  // one look like the stronger one.
  //
  // Neither list writes to the product register. Capture promotes a row into the
  // banks (a to-do or a topic) through the same /research/capture endpoint every
  // other analysis uses; an idea never becomes a product entity, because nobody
  // has validated it.

  const GAP_LEVEL = {
    contested: ["bi-badge--alt", "alert-triangle", "Contested"],
    single: ["bi-badge--manual", "flag", "One competitor"],
    adjacent: ["bi-badge--manual", "git-branch", "Adjacent"],
  };
  const PILL_KIND = { competitor: "comp", partner: "partner", market: "market", us: "us" };
  const DROP_REASON = {
    already_tracked: "already tracked",
    no_source: "not backed by a search result",
    duplicate_in_reply: "proposed twice",
    no_name: "unnamed",
  };

  function ProvPill({ p }) {
    const kind = PILL_KIND[p.kind] || "market";
    return (
      <span className={"bi-prov-pill bi-prov-pill--" + kind} style={{ cursor: "default" }}>
        <I name={(PROV[kind] || PROV.market)[0]} size={12} />{p.name}{p.pricing ? " · " + p.pricing : ""}
      </span>
    );
  }

  // One button, one capture. The label reports what the SERVER answered:
  // "already" means the bank had it, which is not the same as this click
  // storing it, and saying "Added" either way would be a claim about a write
  // that never happened.
  function CaptureBtn({ label, icon, state, onClick }) {
    const done = state === "added" || state === "already";
    const text = state === "busy" ? "Saving…" : state === "added" ? "Added"
      : state === "already" ? "Already there" : state === "failed" ? "Retry" : label;
    return (
      <button className={"bi-btn bi-btn--sm" + (done ? " bi-btn--ai" : "")} disabled={state === "busy" || done} onClick={onClick}>
        <I name={done ? "check" : icon} size={14} />{text}
      </button>
    );
  }

  function GapRow({ g, capState, onCapture }) {
    const [cls, icon, label] = GAP_LEVEL[g.level] || GAP_LEVEL.adjacent;
    return (
      <div style={{ padding: "12px 0", borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--text-primary)" }}>{g.name}</span>
          <span className={"bi-badge " + cls}><I name={icon} size={11} />{label}</span>
          {g.category && <span className="bi-badge bi-badge--manual">{g.category}</span>}
        </div>
        <div style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "5px 0 7px" }}>{g.why}</div>
        <div className="bi-prov-pills" style={{ marginBottom: 8 }}>
          <span className="bi-prov-pills__l">Offered by:</span>
          {g.evidence.map((p, i) => <ProvPill key={i} p={p} />)}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <CaptureBtn label="Add to to-do" icon="check-square" state={capState("todo")} onClick={() => onCapture("todo")} />
          <CaptureBtn label="Capture as topic" icon="message-square" state={capState("topic")} onClick={() => onCapture("topic")} />
        </div>
      </div>
    );
  }

  function IdeaRow({ idea, capState, onCapture, canCapture }) {
    return (
      <div style={{ padding: "12px 0", borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--text-primary)" }}>{idea.name}</span>
          <span className="bi-badge bi-badge--ai"><I name="sparkles" size={11} />Proposal</span>
          {idea.category && <span className="bi-badge bi-badge--manual">{idea.category}</span>}
          {idea.confidence != null && <span className="bi-conf">{idea.confidence}% confident</span>}
        </div>
        {idea.why && <div style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "5px 0 0" }}>{idea.why}</div>}
        {idea.adjacent_to && <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", marginTop: 3 }}>Extends: {idea.adjacent_to}</div>}
        {idea.demand_signal && <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", marginTop: 3 }}>What the source shows: {idea.demand_signal}</div>}
        <div style={{ margin: "7px 0 8px" }}>
          <a className="bi-card__url" href={idea.source_url} target="_blank" rel="noopener noreferrer">
            <I name="link" size={13} />{idea.source_title || idea.source_url}<I name="external-link" size={12} />
          </a>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {canCapture
            ? <>
              <CaptureBtn label="Add to to-do" icon="check-square" state={capState("todo")} onClick={() => onCapture("todo")} />
              <CaptureBtn label="Capture as topic" icon="message-square" state={capState("topic")} onClick={() => onCapture("topic")} />
            </>
            : <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Set up your Business Profile to capture an idea: a captured row has to hang off it.</span>}
        </div>
      </div>
    );
  }

  // The empty cases, kept apart. "No gaps" means three different things and only
  // one of them is good news, so the wording never lets an unread register look
  // like a complete one.
  const GAP_EMPTY = {
    no_products: ["box", "No products tracked yet", "Add offerings first: a gap is what your tracked providers offer and you do not, so with nothing in the register there is nothing to compare."],
    no_providers: ["alert-triangle", "Nothing to compare against", "Offerings are tracked, but none of them records who else provides it. Ingest a competitor's offerings, or add providers by hand, and this comparison starts working."],
    none: ["check-circle", "No gaps found", "Every offering with a tracked provider is one you already sell."],
  };

  function GapsAndIdeas({ site }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [data, setData] = useState(null);
    const [phase, setPhase] = useState("loading");   // loading | ready | error
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(null);          // "gaps" | "ideas" | null
    const [run, setRun] = useState(null);
    const [cap, setCap] = useState({});
    const [open, setOpen] = useState(true);
    const stop = useRef(null);

    const load = useCallback(async () => {
      if (siteId == null) { setData(null); setPhase("ready"); return; }
      setPhase("loading"); setErr("");
      try { setData(await api(`/product-gaps?site_id=${siteId}`)); setPhase("ready"); }
      catch (e) { setErr(e.message || "Couldn't load gaps"); setPhase("error"); }
    }, [siteId]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => () => { if (stop.current) stop.current(); }, []);

    // Gaps are recomputed for free; ideas cost one grounded call, so they are
    // only run when asked for and the last run is read back on reopen.
    const scan = async (withIdeas) => {
      if (siteId == null || busy) return;
      setBusy(withIdeas ? "ideas" : "gaps"); setErr(""); setRun(null);
      const runId = newRunId();
      if (withIdeas) stop.current = pollRun(runId, setRun);
      try {
        const r = await api(`/product-gaps/scan`, {
          method: "POST",
          body: JSON.stringify({ site_id: siteId, ideas: !!withIdeas, run_id: runId }),
        });
        setData(r); setCap({}); setPhase("ready"); setOpen(true);
      } catch (e) {
        setErr(e.message || "Scan failed");
      } finally {
        if (stop.current) { stop.current(); stop.current = null; }
        setBusy(null); setRun(null);
      }
    };

    const capture = async (rowKey, capType, biEntityId, value, meta) => {
      if (siteId == null || biEntityId == null) return;
      const k = rowKey + "|" + capType;
      setCap(c => ({ ...c, [k]: "busy" })); setErr("");
      try {
        const r = await api(`/research/capture`, {
          method: "POST",
          body: JSON.stringify({
            site_id: siteId, entity_type: "product", bi_entity_id: biEntityId,
            capture_type: capType, value, meta,
          }),
        });
        setCap(c => ({ ...c, [k]: r && r.already ? "already" : "added" }));
      } catch (e) {
        setCap(c => ({ ...c, [k]: "failed" }));
        setErr(e.message || "Capture failed");
      }
    };

    if (siteId == null) return null;

    const gaps = (data && data.gaps) || [];
    const ideas = (data && data.ideas) || { status: "never_run", items: [] };
    const ideaItems = Array.isArray(ideas.items) ? ideas.items : [];
    const ownerId = data && data.owner_bi_entity_id;
    const state = (k) => cap[k];

    const gapCapture = (g, capType) => capture(g.key, capType, g.bi_entity_id,
      capType === "todo" ? `Consider offering: ${g.name}` : g.name,
      capType === "todo"
        ? { detail: g.why, priority: g.level === "contested" ? "high" : "medium" }
        : { detail: g.why });
    // An idea hangs off the Business Profile entity: it belongs to the business,
    // not to a product row, and creating a product row for it would put an
    // unvalidated invention in the register.
    const ideaCapture = (idea, capType) => capture(idea.key, capType, ownerId,
      capType === "todo" ? `Consider offering: ${idea.name}` : idea.name,
      capType === "todo"
        ? { detail: [idea.why, `Proposal from a web search: ${idea.source_url}`].filter(Boolean).join(" "), priority: "low" }
        : { detail: [idea.why, idea.source_url].filter(Boolean).join(" ") });

    const dropSummary = (list) => {
      const counts = {};
      (list || []).forEach(d => { counts[d.reason] = (counts[d.reason] || 0) + 1; });
      const parts = Object.keys(counts).map(r => `${counts[r]} ${DROP_REASON[r] || r}`);
      return parts.length ? parts.join(", ") : "";
    };

    const [emptyIcon, emptyTitle, emptyDesc] = GAP_EMPTY[(data && data.status) || "none"] || GAP_EMPTY.none;

    return (
      <div className="bi-page" style={{ paddingTop: 0 }}>
        <div className="bi-detail-section">
          <div className="bi-detail-section__h">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <button className="bi-iconbtn" title={open ? "Collapse" : "Expand"} onClick={() => setOpen(o => !o)}>
                <I name={open ? "chevron-down" : "chevron-right"} size={16} />
              </button>
              Gaps and ideas
              {gaps.length > 0 && <span className="bi-conf">{gaps.length} gap{gaps.length === 1 ? "" : "s"}</span>}
              {ideaItems.length > 0 && <span className="bi-conf">{ideaItems.length} idea{ideaItems.length === 1 ? "" : "s"}</span>}
            </span>
            <span style={{ display: "inline-flex", gap: 6 }}>
              <button className="bi-btn bi-btn--sm" disabled={!!busy} onClick={() => scan(false)}>
                <I name="refresh" size={14} />{busy === "gaps" ? "Comparing…" : "Rescan gaps"}
              </button>
              <button className="bi-btn bi-btn--sm bi-btn--ai" disabled={!!busy} onClick={() => scan(true)}>
                <I name="sparkles" size={14} />{busy === "ideas" ? "Searching…" : "Find ideas"}
              </button>
            </span>
          </div>

          {open && (
            <div className="bi-detail-section__body">
              {err && <div className="bi-subempty" style={{ padding: "12px 0", color: "var(--danger)" }}>{err}</div>}
              {busy === "ideas" && (
                <div className="bi-subempty" style={{ padding: "12px 0" }}>
                  <span className="ml-spinner ml-spinner--sm" style={{ marginRight: 8 }} />
                  {run && run.note ? `${run.label} · ${run.note}` : "Searching the live web. This runs several searches and takes a minute."}
                </div>
              )}

              {/* Gaps: deterministic */}
              <div style={{ paddingTop: 8 }}>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--text-primary)" }}>
                  Gaps <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>· counted from your register, no AI</span>
                </div>
                <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", margin: "3px 0 6px" }}>
                  Offerings a provider you track offers and you do not. Every row shows the named providers it was counted from.
                </p>
                {phase === "loading"
                  ? <div className="bi-subempty" style={{ padding: "16px 0" }}>Loading…</div>
                  : phase === "error"
                    ? <div className="bi-subempty" style={{ padding: "16px 0" }}>Couldn't read the register, so this is unknown rather than empty.</div>
                    : gaps.length === 0
                      ? <div className="bi-subempty" style={{ padding: "16px 0", textAlign: "left" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-secondary)", fontWeight: "var(--weight-semibold)" }}>
                          <I name={emptyIcon} size={16} />{emptyTitle}
                        </div>
                        <div style={{ marginTop: 4 }}>{emptyDesc}</div>
                      </div>
                      : gaps.map(g => (
                        <GapRow key={g.key} g={g}
                          capState={(t) => state(g.key + "|" + t)}
                          onCapture={(t) => gapCapture(g, t)} />
                      ))}
                {data && data.products_tracked > 0 && (
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", padding: "8px 0" }}>
                    Compared {data.comparable} of {data.products_tracked} tracked offering{data.products_tracked === 1 ? "" : "s"} (the ones that record a provider). {data.ours} marked as yours.
                  </div>
                )}
              </div>

              {/* Ideas: a model's proposals */}
              <div style={{ paddingTop: 14, marginTop: 6, borderTop: "1px solid var(--border-default)" }}>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--text-primary)" }}>
                  Ideas <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>· AI proposals, each from a live web search</span>
                </div>
                <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", margin: "3px 0 6px" }}>
                  Not findings. Nothing here is in anyone's register yet, so judge each one against its source before acting.
                </p>

                {ideas.status === "never_run" && (
                  <div className="bi-subempty" style={{ padding: "16px 0", textAlign: "left" }}>
                    No ideas generated yet. "Find ideas" spends one AI call with live web search, and the result is stored so reopening this screen never spends again.
                  </div>
                )}
                {ideas.note && ideas.status !== "never_run" && (
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: "var(--text-xs)", color: "var(--text-secondary)", padding: "8px 0" }}>
                    <I name={ideas.status === "ok" ? "info" : "alert-triangle"} size={14} style={{ flex: "0 0 auto", marginTop: 1 }} />
                    <span>{ideas.note}</span>
                  </div>
                )}
                {ideaItems.map(idea => (
                  <IdeaRow key={idea.key} idea={idea} canCapture={ownerId != null}
                    capState={(t) => state(idea.key + "|" + t)}
                    onCapture={(t) => ideaCapture(idea, t)} />
                ))}
                {ideaItems.length > 0 && (
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", padding: "8px 0" }}>
                    {/* Said out loud, because "web grounded" with no searches behind it is
                        the claim this whole flow exists to avoid making. The counts
                        describe the run these items came from, which is not always the
                        last click: a failed attempt keeps the previous list. */}
                    {ideas.grounded
                      ? `Grounded in ${ideas.searches} live web search${ideas.searches === 1 ? "" : "es"}${ideas.market ? ` on ${ideas.market}` : ""}, ${String(ideas.generated_at || "").slice(0, 10)}.`
                      : "Not web grounded."}
                    {ideas.dropped && ideas.dropped.length > 0 && ` ${ideas.dropped.length} proposal${ideas.dropped.length === 1 ? "" : "s"} discarded: ${dropSummary(ideas.dropped)}.`}
                    {ideas.dead_sources > 0 && ` ${ideas.dead_sources} cited page${ideas.dead_sources === 1 ? "" : "s"} pointed at a domain that does not exist.`}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // The Products screen: the register, then what it implies. Composed here
  // rather than in the shell so the panel travels with the module it belongs to.
  function ProductsScreen({ site, sites, go, detailId, onDetail }) {
    const M = window.MlBiMaster;
    /* Gaps and ideas belong to the REGISTER: they are about the set of
       offerings, what is missing from it and what could join it. Under one
       product's detail view they are about the wrong thing, so the module
       reports which view it is showing and the panel steps aside.
       useCallback because BiModule has it in an effect's dependency list, and a
       fresh function each render would re-fire it on every render. */
    const [view, setView] = useState("list");
    const onViewChange = useCallback((v) => setView(v), []);
    if (!M) return null;
    return (
      <>
        {/* detailId/onDetail are forwarded untouched: which product is open is
            the location's business, and this wrapper only owns the panel that
            steps aside when one is. */}
        <M.BiModule cfg={PRODUCTS} marketSet={true} state="default" site={site} sites={sites} go={go} onViewChange={onViewChange} detailId={detailId} onDetail={onDetail} />
        {view === "list" && <GapsAndIdeas site={site} />}
      </>
    );
  }

  const PRODUCTS = {
    title: "Products", icon: "box", noun: "product", hasUrl: false, ownable: true, shareable: false, addOfferings: true,
    fields: [{ key: "category", label: "Service line" }, { key: "market", label: "Market" }, { key: "pricing", label: "Pricing" }, { key: "positioning", label: "Positioning" }],
    CardComponent: ProductCard, RowComponent: ProductCard, DetailComponent: ProductDetail,
    filters: [
      { label: "Coverage", field: "coverage2", opts: [["offer", "We offer", null], ["gap", "We don't", null], ["contested", "≥2 competitors", null]] },
      { label: "Service line", field: "serviceLineId", opts: [["formation", "Company formation", null], ["tax", "Tax & VAT", null], ["compliance", "Compliance", null]] },
    ],  };

  window.MlBiProducts = { PRODUCTS, ProductsScreen, GapsAndIdeas };
})();
